import { useState, useEffect, useMemo } from 'react';
import { fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { makeStyles } from '@material-ui/core/styles';
import { Content, PageWithHeader, Progress } from '@backstage/core-components';
import { Alert } from '@material-ui/lab';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from '@material-ui/core';

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
  },
  tableContainer: {
    width: '100%',
    overflowX: 'auto',
  },
  controls: {
    marginBottom: 16,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    flexWrap: 'wrap',
  },
  filterControl: {
    minWidth: 280,
  },
  table: {
    minWidth: 980,
    tableLayout: 'fixed',
  },
  idCell: {
    width: 70,
  },
  titleCell: {
    width: '40%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  assetCell: {
    width: '22%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  severityCell: {
    width: 120,
  },
  scoreCell: {
    width: 110,
    whiteSpace: 'nowrap',
  },
  statusCell: {
    width: 130,
    whiteSpace: 'nowrap',
  },
  groupHeaderCell: {
    backgroundColor: '#f7fbff',
    borderTop: '1px solid #e4edf5',
    borderBottom: '1px solid #e4edf5',
    fontWeight: 600,
  },
}));

interface Finding {
  id: number;
  title: string;
  severity: string;
  status: string;
  asset: string;
  riskScore: number;
}

interface FindingEndpoint {
  protocol?: string;
  host?: string;
  path?: string;
}

interface RawFinding {
  id: number;
  title?: string;
  severity?: string;
  status?: string;
  active?: boolean;
  verified?: boolean;
  false_p?: boolean;
  risk_accepted?: boolean;
  numerical_severity?: number | string;
  cvssv3_score?: number | string;
  cvssv2_score?: number | string;
  component_name?: string;
  service?: string;
  endpoint?: string;
  file_path?: string;
  endpoints?: FindingEndpoint[];
  product?: unknown;
  product_name?: string;
  engagement?: unknown;
  engagement_name?: string;
  test?: unknown;
}

interface ProductDetail {
  id: number;
  name?: string;
}

interface EngagementDetail {
  id: number;
  name?: string;
  product?: unknown;
}

interface TestDetail {
  id: number;
  title?: string;
  engagement?: unknown;
}

const MAX_FINDINGS = 200;

const severityRank: Record<string, number> = {
  Critical: 4,
  High: 3,
  Medium: 2,
  Low: 1,
  Info: 0,
  Informational: 0,
};

const severityColors: { [key: string]: 'default' | 'primary' | 'secondary' } = {
  Critical: 'secondary',
  High: 'secondary',
  Medium: 'primary',
  Low: 'default',
};

const toNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return undefined;
};

const getNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return undefined;
};

const getIdFromUnknown = (value: unknown): number | undefined => {
  const direct = getNumber(value);
  if (direct !== undefined) {
    return direct;
  }

  if (typeof value === 'string') {
    const match = value.match(/\/(\d+)\/?$/);
    if (match?.[1]) {
      return Number(match[1]);
    }
  }

  if (value && typeof value === 'object') {
    const asRecord = value as Record<string, unknown>;
    const fromId = getNumber(asRecord.id);
    if (fromId !== undefined) {
      return fromId;
    }

    if (typeof asRecord.url === 'string') {
      const match = asRecord.url.match(/\/(\d+)\/?$/);
      if (match?.[1]) {
        return Number(match[1]);
      }
    }
  }

  return undefined;
};

const getNameFromUnknown = (value: unknown): string | undefined => {
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  const asRecord = value as Record<string, unknown>;
  if (typeof asRecord.name === 'string' && asRecord.name.trim()) {
    return asRecord.name;
  }

  if (typeof asRecord.title === 'string' && asRecord.title.trim()) {
    return asRecord.title;
  }

  return undefined;
};

const fetchJson = async (
  fetchApi: { fetch: (input: string, init?: RequestInit) => Promise<Response> },
  url: string,
): Promise<unknown | null> => {
  try {
    const response = await fetchApi.fetch(url, {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      return null;
    }

    return await response.json();
  } catch {
    return null;
  }
};

const isGenericEngagementName = (value?: string): boolean => {
  if (!value) {
    return false;
  }

  const normalized = value.trim().toLowerCase();
  if (!normalized) {
    return false;
  }

  return (
    normalized === 'main' ||
    normalized === 'master' ||
    normalized === 'develop' ||
    normalized === 'development' ||
    normalized === 'trunk' ||
    normalized === 'default' ||
    normalized === 'latest' ||
    normalized.startsWith('refs/heads/')
  );
};

const getMeaningfulAssetCandidate = (value?: string): string | undefined => {
  if (!value) {
    return undefined;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  return isGenericEngagementName(trimmed) ? undefined : trimmed;
};

const buildAssetMaps = async (
  fetchApi: { fetch: (input: string, init?: RequestInit) => Promise<Response> },
  rawFindings: RawFinding[],
): Promise<{
  productById: Map<number, string>;
  engagementById: Map<number, { name: string; productId?: number }>;
  testById: Map<number, { title: string; engagementId?: number }>;
}> => {
  const productById = new Map<number, string>();
  const engagementById = new Map<number, { name: string; productId?: number }>();
  const testById = new Map<number, { title: string; engagementId?: number }>();

  const productIds = new Set<number>();
  const engagementIds = new Set<number>();
  const testIds = new Set<number>();

  for (const finding of rawFindings) {
    const productId = getIdFromUnknown(finding.product);
    const engagementId = getIdFromUnknown(finding.engagement);
    const testId = getIdFromUnknown(finding.test);

    const inlineProductName = getNameFromUnknown(finding.product);
    if (productId !== undefined && inlineProductName) {
      productById.set(productId, inlineProductName);
    }

    const inlineEngagementName =
      getNameFromUnknown(finding.engagement) || finding.engagement_name;
    if (engagementId !== undefined && inlineEngagementName) {
      engagementById.set(engagementId, {
        name: inlineEngagementName,
        productId,
      });
    }

    if (productId !== undefined) {
      productIds.add(productId);
    }

    if (engagementId !== undefined) {
      engagementIds.add(engagementId);
    }

    if (testId !== undefined) {
      testIds.add(testId);
    }
  }

  const testDetails = await Promise.all(
    [...testIds].map(async id => {
      const data = await fetchJson(fetchApi, `/api/defectdojo/tests/${id}/`);
      if (!data || typeof data !== 'object') {
        return null;
      }

      const testData = data as TestDetail;
      return {
        id,
        title: testData.title,
        engagementId: getIdFromUnknown(testData.engagement),
      };
    }),
  );

  for (const test of testDetails) {
    if (!test) {
      continue;
    }

    testById.set(test.id, {
      title: test.title || `Test ${test.id}`,
      engagementId: test.engagementId,
    });

    if (test.engagementId !== undefined) {
      engagementIds.add(test.engagementId);
    }
  }

  const engagementDetails = await Promise.all(
    [...engagementIds].map(async id => {
      const data = await fetchJson(fetchApi, `/api/defectdojo/engagements/${id}/`);
      if (!data || typeof data !== 'object') {
        return null;
      }

      const engagementData = data as EngagementDetail;
      return {
        id,
        name: engagementData.name,
        productId: getIdFromUnknown(engagementData.product),
      };
    }),
  );

  for (const engagement of engagementDetails) {
    if (!engagement) {
      continue;
    }

    engagementById.set(engagement.id, {
      name: engagement.name || `Engagement ${engagement.id}`,
      productId: engagement.productId,
    });

    if (engagement.productId !== undefined) {
      productIds.add(engagement.productId);
    }
  }

  const productDetails = await Promise.all(
    [...productIds].map(async id => {
      const data = await fetchJson(fetchApi, `/api/defectdojo/products/${id}/`);
      if (!data || typeof data !== 'object') {
        return null;
      }

      const productData = data as ProductDetail;
      return {
        id,
        name: productData.name,
      };
    }),
  );

  for (const product of productDetails) {
    if (!product || !product.name) {
      continue;
    }

    productById.set(product.id, product.name);
  }

  return { productById, engagementById, testById };
};

const getAsset = (
  raw: RawFinding,
  maps: {
    productById: Map<number, string>;
    engagementById: Map<number, { name: string; productId?: number }>;
    testById: Map<number, { title: string; engagementId?: number }>;
  },
): string => {
  const engagementLabel = getMeaningfulAssetCandidate(raw.engagement_name);

  const productName = getMeaningfulAssetCandidate(raw.product_name);
  if (productName) {
    return productName;
  }

  const productId = getNumber(raw.product);
  const rawProductName = getMeaningfulAssetCandidate(
    getNameFromUnknown(raw.product),
  );
  if (rawProductName) {
    return rawProductName;
  }

  const resolvedProductId = getIdFromUnknown(raw.product);
  if (resolvedProductId !== undefined && maps.productById.has(resolvedProductId)) {
    return maps.productById.get(resolvedProductId) || `Product ${resolvedProductId}`;
  }

  if (productId !== undefined && maps.productById.has(productId)) {
    return maps.productById.get(productId) || `Product ${productId}`;
  }

  const engagementId = getIdFromUnknown(raw.engagement);
  const rawEngagementName = getMeaningfulAssetCandidate(
    getNameFromUnknown(raw.engagement),
  );
  if (rawEngagementName) {
    return rawEngagementName;
  }

  if (engagementId !== undefined) {
    const engagement = maps.engagementById.get(engagementId);
    if (engagement?.productId && maps.productById.has(engagement.productId)) {
      return maps.productById.get(engagement.productId) || `Product ${engagement.productId}`;
    }

    const engagementName = getMeaningfulAssetCandidate(engagement?.name);
    if (engagementName) {
      return engagementName;
    }
  }

  const testId = getIdFromUnknown(raw.test);
  if (testId !== undefined) {
    const test = maps.testById.get(testId);
    if (test?.engagementId) {
      const engagement = maps.engagementById.get(test.engagementId);
      const engagementName = getMeaningfulAssetCandidate(engagement?.name);
      if (engagementName) {
        return engagementName;
      }
      if (engagement?.productId && maps.productById.has(engagement.productId)) {
        return maps.productById.get(engagement.productId) || `Product ${engagement.productId}`;
      }
    }

    if (test?.title) {
      return test.title;
    }
  }

  if (engagementLabel && !isGenericEngagementName(engagementLabel)) {
    return engagementLabel;
  }

  const endpoint = raw.endpoints?.[0];
  if (endpoint) {
    const host = endpoint.host ?? '';
    const path = endpoint.path ?? '';
    const protocol = endpoint.protocol ? `${endpoint.protocol}://` : '';
    const endpointAsset = `${protocol}${host}${path}`.trim();
    if (endpointAsset) {
      return endpointAsset;
    }
  }

  const fallbackAsset =
    getMeaningfulAssetCandidate(raw.component_name) ||
    getMeaningfulAssetCandidate(raw.service) ||
    getMeaningfulAssetCandidate(raw.endpoint) ||
    getMeaningfulAssetCandidate(raw.file_path);

  return fallbackAsset || 'Unmapped asset';
};

const getStatus = (raw: RawFinding): string => {
  if (raw.status) {
    return raw.status;
  }

  if (raw.false_p) {
    return 'False Positive';
  }

  if (raw.risk_accepted) {
    return 'Risk Accepted';
  }

  if (raw.active === false) {
    return 'Inactive';
  }

  if (raw.verified) {
    return 'Verified';
  }

  return 'Active';
};

const getRiskScore = (raw: RawFinding): number => {
  const numericalSeverity = toNumber(raw.numerical_severity);
  if (numericalSeverity !== undefined) {
    return numericalSeverity;
  }

  const cvssV3 = toNumber(raw.cvssv3_score);
  if (cvssV3 !== undefined) {
    return cvssV3;
  }

  const cvssV2 = toNumber(raw.cvssv2_score);
  if (cvssV2 !== undefined) {
    return cvssV2;
  }

  return severityRank[raw.severity ?? ''] ?? 0;
};

const normalizeFinding = (
  raw: RawFinding,
  maps: {
    productById: Map<number, string>;
    engagementById: Map<number, { name: string; productId?: number }>;
    testById: Map<number, { title: string; engagementId?: number }>;
  },
): Finding => {
  const severity = raw.severity ?? 'Unknown';

  return {
    id: raw.id,
    title: raw.title ?? `Finding ${raw.id}`,
    severity,
    status: getStatus(raw),
    asset: getAsset(raw, maps),
    riskScore: getRiskScore(raw),
  };
};

export const DefectDojoPage = () => {
  const classes = useStyles();
  const fetchApi = useApi(fetchApiRef);
  const [findings, setFindings] = useState<Finding[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const assetOptions = useMemo(() => {
    return [...new Set(findings.map(finding => finding.asset))].sort((a, b) =>
      a.localeCompare(b),
    );
  }, [findings]);

  const visibleFindings = useMemo(() => {
    if (selectedAsset === 'all') {
      return findings;
    }

    return findings.filter(finding => finding.asset === selectedAsset);
  }, [findings, selectedAsset]);

  const groupedFindings = useMemo(() => {
    const groups = new Map<string, Finding[]>();

    for (const finding of visibleFindings) {
      const existing = groups.get(finding.asset) ?? [];
      existing.push(finding);
      groups.set(finding.asset, existing);
    }

    return [...groups.entries()]
      .map(([asset, assetFindings]) => ({
        asset,
        findings: assetFindings.sort((a, b) => b.riskScore - a.riskScore),
      }))
      .sort((a, b) => {
        if (b.findings.length !== a.findings.length) {
          return b.findings.length - a.findings.length;
        }

        return a.asset.localeCompare(b.asset);
      });
  }, [visibleFindings]);

  const handleAssetChange = (event: any) => {
    setSelectedAsset(String(event.target.value));
  };

  useEffect(() => {
    const fetchFindings = async () => {
      try {
        const response = await fetchApi.fetch(
          '/api/defectdojo/findings/?limit=500&active=true&false_p=false',
          {
            headers: {
              Accept: 'application/json',
            },
          },
        );

        const contentType = response.headers.get('content-type') ?? '';
        const isJson = contentType.includes('application/json');
        const rawBody = await response.text();

        let data: unknown = null;
        if (isJson) {
          try {
            data = JSON.parse(rawBody);
          } catch {
            throw new Error(
              `DefectDojo API returned invalid JSON (${response.status}).`,
            );
          }
        }

        console.log('DefectDojo API response:', {
          status: response.status,
          contentType,
          isJson,
          data,
        });

        if (!response.ok) {
          const errorMsg = isJson
            ? typeof data === 'string'
              ? data
              : JSON.stringify(data)
            : rawBody.slice(0, 400);
          throw new Error(
            `DefectDojo API error: ${response.status} (${contentType || 'unknown content type'}) - ${errorMsg}`,
          );
        }

        if (!isJson || typeof data !== 'object' || data === null) {
          throw new Error(
            `DefectDojo API returned non-JSON success response (${contentType || 'unknown content type'}).`,
          );
        }

        const results = (data as { results?: unknown }).results;
        const rawFindings = Array.isArray(results)
          ? (results as RawFinding[])
          : [];
        const assetMaps = await buildAssetMaps(fetchApi, rawFindings);

        const prioritized = rawFindings
          .map(finding => normalizeFinding(finding, assetMaps))
          .filter(finding => severityRank[finding.severity] >= 3)
          .sort((a, b) => {
            if (b.riskScore !== a.riskScore) {
              return b.riskScore - a.riskScore;
            }

            return (
              (severityRank[b.severity] ?? 0) -
              (severityRank[a.severity] ?? 0)
            );
          })
          .slice(0, MAX_FINDINGS);

        setFindings(prioritized);
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        console.error('DefectDojo fetch error:', message);
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchFindings();
  }, [fetchApi]);

  if (loading) {
    return (
      <PageWithHeader title="DefectDojo" themeId="security">
        <Content>
          <Progress />
        </Content>
      </PageWithHeader>
    );
  }

  return (
    <PageWithHeader title="DefectDojo" themeId="security">
      <Content className={classes.root}>
        {error && (
          <Alert severity="error">
            {error}
          </Alert>
        )}

        {!error && (
          <>
            <Box className={classes.controls}>
              <Typography variant="body2" color="textSecondary">
                Showing {visibleFindings.length} high-risk findings across {groupedFindings.length} assets
              </Typography>
              <FormControl
                variant="outlined"
                size="small"
                className={classes.filterControl}
              >
                <InputLabel id="asset-filter-label">Filter by asset</InputLabel>
                <Select
                  labelId="asset-filter-label"
                  value={selectedAsset}
                  onChange={handleAssetChange}
                  label="Filter by asset"
                >
                  <MenuItem value="all">All assets</MenuItem>
                  {assetOptions.map(asset => (
                    <MenuItem key={asset} value={asset}>
                      {asset}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <TableContainer component={Paper} className={classes.tableContainer}>
              <Table className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.idCell}>ID</TableCell>
                    <TableCell className={classes.titleCell}>Title</TableCell>
                    <TableCell className={classes.assetCell}>Asset</TableCell>
                    <TableCell className={classes.severityCell}>Severity</TableCell>
                    <TableCell className={classes.scoreCell}>Risk Score</TableCell>
                    <TableCell className={classes.statusCell}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleFindings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No high-risk findings found
                      </TableCell>
                    </TableRow>
                  ) : (
                    groupedFindings.flatMap(group => [
                      <TableRow key={`group-${group.asset}`}>
                        <TableCell colSpan={6} className={classes.groupHeaderCell}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <span>{group.asset}</span>
                            <Chip
                              size="small"
                              color="primary"
                              label={`${group.findings.length} finding${group.findings.length === 1 ? '' : 's'}`}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>,
                      ...group.findings.map(finding => (
                        <TableRow key={finding.id}>
                          <TableCell className={classes.idCell}>{finding.id}</TableCell>
                          <TableCell className={classes.titleCell} title={finding.title}>
                            {finding.title}
                          </TableCell>
                          <TableCell className={classes.assetCell} title={finding.asset}>
                            {finding.asset}
                          </TableCell>
                          <TableCell className={classes.severityCell}>
                            <Chip
                              label={finding.severity}
                              color={
                                severityColors[finding.severity] || 'default'
                              }
                              size="small"
                            />
                          </TableCell>
                          <TableCell className={classes.scoreCell}>
                            {finding.riskScore.toFixed(1)}
                          </TableCell>
                          <TableCell className={classes.statusCell}>{finding.status}</TableCell>
                        </TableRow>
                      )),
                    ])
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Content>
    </PageWithHeader>
  );
};